<template>
  <div ref="pointCloudContainer"></div>
</template>

<script setup lang="ts">
import { computed, onMounted, onUnmounted, watch, shallowRef } from 'vue'
import * as THREE from 'three'
import { normalizeIntensity } from './normalizeUtils'

// 导入类型定义
import type { NormalizedPointCloud } from './types'

// Props 接口定义
interface RadarPointCloudProps {
  points: NormalizedPointCloud[]
  size: number
  opacity: number
}

const props = withDefaults(defineProps<RadarPointCloudProps>(), {
  points: () => [],
  size: 0.03,
  opacity: 0.6,
})

// 暴露给父组件的引用，用于添加到场景中
const groupRef = shallowRef(new THREE.Group())

// 归一化处理points
const normalizedPoints = computed(() => {
  return normalizeIntensity(props.points || [])
})

// 更新点云
const updatePointCloud = () => {
  const { size, opacity } = props
  const points = normalizedPoints.value

  // console.log('Updating point cloud with', points.length)

  // 清空现有网格
  while (groupRef.value.children.length > 0) {
    const mesh = groupRef.value.children[0]
    groupRef.value.remove(mesh)
    // 释放几何体和材质资源
    mesh.geometry.dispose()
    mesh.material.dispose()
  }

  // 创建新的点云
  points.forEach((p) => {
    const x = p[0] / 100
    const y = p[1] / 100
    const z = p[2] / 100
    const intensity = p.normalizedIntensity

    // 计算颜色 米白色 #F5F5DC
    const baseColor = new THREE.Color('#F5F5DC')
    // 根据intensity调整颜色亮度，intensity越高颜色越亮
    const color = baseColor.clone().multiplyScalar(0.8 + 0.4 * intensity)

    const geometry = new THREE.SphereGeometry(size, 16, 16)
    const material = new THREE.MeshStandardMaterial({
      color: color,
      transparent: true,
      opacity: opacity,
    })

    const mesh = new THREE.Mesh(geometry, material)
    mesh.position.set(x, y, z)
    groupRef.value.add(mesh)
  })
}

// 监听points变化
watch(
  () => props.points,
  () => {
    updatePointCloud()
  },
  { deep: true }
)

// 监听size变化
watch(
  () => props.size,
  () => {
    updatePointCloud()
  }
)

// 监听opacity变化
watch(
  () => props.opacity,
  () => {
    updatePointCloud()
  }
)

// 初始化
onMounted(() => {
  updatePointCloud()
})

// 清理
onUnmounted(() => {
  // 清空所有网格并释放资源
  while (groupRef.value.children.length > 0) {
    const mesh = groupRef.value.children[0]
    groupRef.value.remove(mesh)
    mesh.geometry.dispose()
    mesh.material.dispose()
  }
})

// 暴露group给父组件
defineExpose({
  group: groupRef,
})
</script>

<style scoped lang="less"></style>
